import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import { 
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes
} from 'aws-amplify/auth';

interface User {
  id: string;
  email: string;
  name?: string;
}

interface AuthError {
  code: string;
  message: string;
  name: string;
}

type AuthState = 'idle' | 'checking' | 'signing-in' | 'signed-in' | 'signed-out' | 'error';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [authState, setAuthState] = useState<AuthState>('idle');
  
  // Refs to prevent multiple simultaneous operations
  const activeSignInRequest = useRef<Promise<void> | null>(null);
  const initialCheckCompleted = useRef<boolean>(false);
  const stateChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced state change function to prevent rapid authentication state changes
  const setAuthStateDebounced = useCallback((newState: AuthState) => {
    if (stateChangeTimeoutRef.current) {
      clearTimeout(stateChangeTimeoutRef.current);
    }
    
    stateChangeTimeoutRef.current = setTimeout(() => {
      setAuthState(newState);
    }, 50); // Small delay to batch rapid state changes
  }, []);

  // Initial authentication check - runs only once
  useEffect(() => {
    if (!initialCheckCompleted.current) {
      setAuthStateDebounced('checking');
      checkAuthState().finally(() => {
        initialCheckCompleted.current = true;
        setAuthStateDebounced('idle');
      });
    }
  }, []); // Empty dependency array - runs only once

  // Separate effect for token refresh - only runs when user is authenticated
  useEffect(() => {
    if (authState === 'signed-in' && user) {
      const refreshInterval = setInterval(() => {
        refreshSession().catch(console.error);
      }, 15 * 60 * 1000); // Refresh every 15 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [authState, user]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (stateChangeTimeoutRef.current) {
        clearTimeout(stateChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleAuthError = (error: any): AuthError => {
    const authError: AuthError = {
      code: error.name || error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      name: error.name || 'AuthError'
    };

    // Map common Cognito errors to user-friendly messages
    switch (authError.code) {
      case 'UserNotConfirmedException':
        authError.message = 'Please check your email and confirm your account before signing in.';
        break;
      case 'NotAuthorizedException':
        authError.message = 'Incorrect email or password. Please try again.';
        break;
      case 'UserNotFoundException':
        authError.message = 'Incorrect email or password. Please try again.';
        break;
      case 'InvalidPasswordException': {
        const policyMatch = authError.message.match(/policy:\s*(.+)$/i);
        authError.message = policyMatch
          ? policyMatch[1].trim()
          : 'Password does not meet requirements.';
        break;
      }
      case 'UsernameExistsException':
        authError.message = 'An account with this email already exists.';
        break;
      case 'CodeMismatchException':
        authError.message = 'Invalid verification code. Please try again.';
        break;
      case 'ExpiredCodeException':
        authError.message = 'Verification code has expired. Please request a new one.';
        break;
      case 'LimitExceededException':
        authError.message = 'Too many attempts. Please try again later.';
        break;
      case 'NetworkError':
        authError.message = 'Network error. Please check your connection and try again.';
        break;
      default:
        // Keep the original message for unknown errors
        break;
    }

    setError(authError);
    return authError;
  };

  const clearError = () => {
    setError(null);
  };

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      clearError();
      
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (session.tokens?.accessToken) {
        const attrs = await fetchUserAttributes();
        const email = currentUser.signInDetails?.loginId || '';
        setUser({
          id: currentUser.userId,
          email,
          name: attrs.given_name || email,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.log('No authenticated user found');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    // Prevent multiple simultaneous sign-in requests
    if (activeSignInRequest.current) {
      return activeSignInRequest.current;
    }

    const performSignIn = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setAuthStateDebounced('signing-in');
        clearError();
        
        const result = await amplifySignIn({ username: email, password });
        
        if (result.isSignedIn) {
          const currentUser = await getCurrentUser();
          const attrs = await fetchUserAttributes();
          const userInfo = {
            id: currentUser.userId,
            email: email,
            name: attrs.given_name || email,
          };
          
          setUser(userInfo);
          setAuthStateDebounced('signed-in');
        } else {
          throw new Error('Sign in was not completed');
        }
      } catch (error) {
        setAuthStateDebounced('error');
        const authError = handleAuthError(error);
        throw authError;
      } finally {
        setIsLoading(false);
      }
    };

    // Set and execute the active request
    activeSignInRequest.current = performSignIn();
    
    try {
      await activeSignInRequest.current;
    } finally {
      activeSignInRequest.current = null;
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      clearError();
      
      await amplifySignOut();
      setUser(null);
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      clearError();
      
      await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName || '',
            family_name: lastName || '',
          },
        },
      });
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    try {
      clearError();
      
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    }
  };

  const resendConfirmationCode = async (email: string) => {
    try {
      clearError();
      
      await resendSignUpCode({ username: email });
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      clearError();
      
      await resetPassword({ username: email });
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    }
  };

  const confirmPassword = async (email: string, code: string, newPassword: string) => {
    try {
      clearError();
      
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    } catch (error) {
      const authError = handleAuthError(error);
      throw authError;
    }
  };

  const refreshSession = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (!session.tokens?.accessToken) {
        throw new Error('No valid session found');
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      // If refresh fails, sign out the user
      await signOut();
      throw handleAuthError(error);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    signIn,
    signOut,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmPassword,
    refreshSession,
    clearError,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
